/*
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 *
 */

import $ from "$/jquery";

import ui from "$/io.ox/core/desktop";

import { settings as docsSettings } from "@/io.ox/office/settings";
import { createIcon } from "@/io.ox/office/tk/dom";
import { globalLogger } from "@/io.ox/office/tk/utils/logger";
import { BROWSER_TAB_SUPPORT, createDocsURL } from "@/io.ox/office/tk/utils/tabutils";

import { AppType, GLOBAL_APP_CONFIGS } from "@/io.ox/office/baseframework/utils/apputils";
import { type AppFactoryLoader, registerAppModule } from "@/io.ox/office/baseframework/appregistry";
import type { BaseAppFactory } from "@/io.ox/office/baseframework/app/appfactory";

import type { PortalAppFactory } from "@/io.ox/office/portal/app/appfactory";

// public functions ===========================================================

export async function registerDocumentsApps(): Promise<void> {

    // whether something has been changed in the settings (settings need to be saved afterwards)
    let settingsChanged = false;

    // configuration of all Documents editor applications
    const EDITOR_APP_LOADERS: Record<AppType, AppFactoryLoader<BaseAppFactory<any>>> = {
        [AppType.TEXT]:         () => import("@/io.ox/office/text/app/appfactory"),
        [AppType.SPREADSHEET]:  () => import("@/io.ox/office/spreadsheet/app/appfactory"),
        [AppType.PRESENTATION]: () => import("@/io.ox/office/presentation/app/appfactory"),
    };

    // create a stub for each Documents portal and editor application
    for (const appConfig of GLOBAL_APP_CONFIGS) {

        // static application configuration
        const { appType, appIcon, appTitle, portalCapabilities, portalModulePath, editorModulePath } = appConfig;

        // the factory loader function for all portal apps
        const portalFactoryLoader: AppFactoryLoader<PortalAppFactory> = async () => {
            const { PortalAppFactory } = await import("@/io.ox/office/portal/app/appfactory");
            // create a subclass with parameter-less constructor
            class Factory extends PortalAppFactory {
                constructor() { super(appConfig); }
            }
            // return a module-like object with a "default export"
            return { default: Factory };
        };

        // register the application module (returns an application loader)
        const portalAppLoader = registerAppModule(portalModulePath, portalFactoryLoader);

        // create the portal application stub used for the launcher control, and for auto launch/restore
        ui.createApp({
            id: portalModulePath,
            name: portalModulePath,
            title: appTitle,
            icon: $(createIcon(appIcon)),
            requires: portalCapabilities,
            settings: false,
            refreshable: true,
            openInTab: BROWSER_TAB_SUPPORT,
            tabUrl: createDocsURL(appType, { app: portalModulePath }),
            load: portalAppLoader,
        });

        // register the application module (returns an application loader)
        const editorAppLoader = registerAppModule(editorModulePath, EDITOR_APP_LOADERS[appType]);

        // create the editor application stub used for auto launch/restore
        ui.createApp({
            id: editorModulePath,
            name: editorModulePath,
            requires: `${appType} infostore`,
            settings: false,
            refreshable: true,
            load: editorAppLoader,
        });

        // Bug 62755, DOCS-3611, ...
        //
        // When creating a new document, or opening an existing document, backend wants to update the settings
        // property "portal/<apptype>/recents" containing the "Recent Documents" list. If this property is not
        // present at all, the entire "portal/<apptype>" subtree will be re-created, effectively deleting all
        // other existing properties there, for example "portal/<apptype>/fulltour/shown" which causes to show
        // the portal tours twice.
        //
        // Workaround (hacky!): Create the settings subtree with this property before starting the portal
        // application.
        //
        // DOCS-3698: This must be done very early (!) in order to not conflict with portal welcome tours
        // starting quickly, e.g. in automated test scenarios.
        //
        const configKey = `portal/${appType}/recents`;
        if (docsSettings.get(configKey) === undefined) {
            docsSettings.set(configKey, "[]", { silent: true });
            settingsChanged = true;
        }
    }

    // save settings if required (await to prevent any possible race condition with other settings accesses)
    if (settingsChanged) {
        globalLogger.info("$badge{launch} saving updated configuration");
        await docsSettings.save(undefined, { force: true });
    }
}
